import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import {
  useGetInternByid,
  useGetAllSupervisors,
  useAssignSupervisor,
  useGetAllDepartments,
  useExternalStaffLookup,
} from "../../adapters/Requests";
import { formatDuration } from "../../adapters/utils";
import useAuth from "../../hooks/useAuth";
import { Loader2 } from "lucide-react";

export default function AssignSupervisorViewer({ internId, onClose }) {
  const { auth } = useAuth();

  const { data: internDataObj, isLoading: isInternLoading } =
    useGetInternByid(internId);
  const internData = internDataObj?.data || internDataObj;

  const { data: allSupervisors = [], isLoading: loadingAllSupervisors } =
    useGetAllSupervisors();
  const assignSupervisorMutation = useAssignSupervisor();
  const { data: departmentsData } = useGetAllDepartments();

  // Create departments lookup map
  const departmentsMap = useMemo(() => {
    const map = {};
    if (departmentsData?.departments) {
      departmentsData.departments.forEach((dept) => {
        map[dept.code] = dept.name;
      });
    }
    return map;
  }, [departmentsData]);

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const [searchMode, setSearchMode] = useState("name");
  const [lookupInput, setLookupInput] = useState("");
  const [debouncedName, setDebouncedName] = useState("");
  const [selectedSupervisor, setSelectedSupervisor] = useState(null);
  const closeTimeoutRef = useRef(null);

  const currentSupervisorId =
    internData?.InternPlacements?.[0]?.supervising_officer_staff_id;
  const hasSupervisor = currentSupervisorId != null;
  const currentSupervisor = allSupervisors.find(
    (s) => s.staff_id === currentSupervisorId,
  );
  const supervisorDisplayName = currentSupervisor
    ? `${currentSupervisor.first_name} ${currentSupervisor.last_name} (${currentSupervisor.staff_id})`
    : currentSupervisorId;

  const canAssign =
    auth?.permissions?.includes("assign_supervisor") ||
    auth?.roles?.includes("representative");

  const handleOpenAssignDialog = () => {
    setIsDialogOpen(true);
    setSearchMode("name");
    setLookupInput("");
    setDebouncedName("");
    setSelectedSupervisor(null);
  };

  const handleSelectSupervisor = (supervisor) => {
    setSelectedSupervisor(supervisor);
  };

  const handleConfirmAssignment = async () => {
    if (!selectedSupervisor) return;

    try {
      const staffId = (
        selectedSupervisor.staffId ||
        selectedSupervisor.staff_id ||
        ""
      ).trim();
      const firstName =
        selectedSupervisor.firstName || selectedSupervisor.first_name || "";
      const middleName =
        selectedSupervisor.middleName || selectedSupervisor.middle_name || "";
      const lastName =
        selectedSupervisor.lastName || selectedSupervisor.last_name || "";
      const fullName =
        selectedSupervisor.fullName ||
        [firstName, middleName, lastName].filter(Boolean).join(" ");
      const businessUnitCode = selectedSupervisor.divisionCode || "";
      const departmentCode = selectedSupervisor.deptCode || "";
      const location = (
        selectedSupervisor.location ||
        selectedSupervisor.locationName ||
        ""
      ).trim();

      if (
        !staffId ||
        !fullName ||
        !businessUnitCode ||
        !departmentCode ||
        !location
      ) {
        toast.error(
          "Cannot assign supervisor. Missing required staff details (staff ID, name, business unit, or location).",
        );
        setIsDialogOpen(false);
        setSelectedSupervisor(null);
        return;
      }

      const payload = {
        staff_id: staffId,
        staff_name: fullName,
        staff_email: selectedSupervisor.email || "",
        business_unit_code: businessUnitCode,
        department_code: departmentCode,
        location,
        role: "supervisor",
      };

      await assignSupervisorMutation.mutateAsync({ internId, payload });

      setIsDialogOpen(false);
      setSelectedSupervisor(null);
      toast.success(
        hasSupervisor
          ? "Supervisor reassigned successfully!"
          : "Supervisor assigned successfully!",
      );

      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
      closeTimeoutRef.current = setTimeout(() => {
        if (onClose) onClose();
      }, 1500);
    } catch (error) {
      console.error(error);
      const errorMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        "Unknown error";

      toast.error(errorMessage);
      setIsDialogOpen(false);
      setSelectedSupervisor(null);
    }
  };

  const institutionName =
    internData?.application?.ApplicationsAcademicQualification?.institution
      ?.name ||
    internData?.application?.ApplicationNYSCDetails?.institution?.name ||
    "N/A";

  const duration =
    internData?.application?.Applicant?.InternshipProgram?.[0]
      ?.duration_in_months;
  const formattedDuration = formatDuration(duration);

  useEffect(() => {
    if (searchMode !== "name") {
      setDebouncedName("");
      return;
    }

    const handle = setTimeout(() => {
      setDebouncedName(lookupInput.trim());
    }, 450);

    return () => clearTimeout(handle);
  }, [lookupInput, searchMode]);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  const lookupParams = useMemo(() => {
    if (searchMode === "staffId") {
      const trimmedId = lookupInput.trim();
      return trimmedId ? { staffId: trimmedId } : null;
    }

    const trimmedName = debouncedName.trim();
    if (trimmedName.length < 2) return null;
    return { name: trimmedName };
  }, [searchMode, lookupInput, debouncedName]);

  const {
    data: lookupResponse,
    isLoading: isLookupLoading,
    error: lookupError,
  } = useExternalStaffLookup(lookupParams);

  const externalLookupResults = lookupResponse?.data || [];

  if (isInternLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 text-green-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-full px-1 py-2 overflow-y-auto">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900">
          Assign Supervisor
        </h2>
      </div>

      <div className="space-y-6">
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <h3 className="text-sm font-bold text-gray-900 mb-4 border-b pb-2">
            {hasSupervisor ? "CURRENT ASSIGNMENT" : "ASSIGNMENT STATUS"}
          </h3>

          <div className="space-y-4">
            {hasSupervisor && (
              <div className="bg-yellow-50 p-3 rounded border border-yellow-200">
                <label className="text-xs font-bold text-yellow-800 block mb-1">
                  Current Supervisor
                </label>
                <span className="text-sm font-medium text-gray-900">
                  {supervisorDisplayName}
                </span>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-gray-500 uppercase font-bold">
                  Intern Name
                </label>
                <div className="text-sm font-medium text-gray-900">
                  {internData?.first_name} {internData?.last_name}
                </div>
              </div>

              <div>
                <label className="text-xs text-gray-500 uppercase font-bold">
                  Application Code
                </label>
                <div className="text-sm font-medium text-gray-900 font-mono">
                  {internData?.application?.code || "N/A"}
                </div>
              </div>

              <div>
                <label className="text-xs text-gray-500 uppercase font-bold">
                  Email Address
                </label>
                <div className="text-sm font-medium text-gray-900">
                  {internData?.email_address || "N/A"}
                </div>
              </div>

              <div>
                <label className="text-xs text-gray-500 uppercase font-bold">
                  Phone Number
                </label>
                <div className="text-sm font-medium text-gray-900">
                  {internData?.phone_number ||
                    internData?.application?.Applicant?.phone_number ||
                    "N/A"}
                </div>
              </div>

              <div>
                <label className="text-xs text-gray-500 uppercase font-bold">
                  School ID / Matric Number
                </label>
                <div className="text-sm font-medium text-gray-900">
                  {internData?.application?.ApplicationsAcademicQualification
                    ?.matric_number ||
                    internData?.application?.ApplicationNYSCDetails
                      ?.call_up_number ||
                    "N/A"}
                </div>
              </div>

              <div>
                <label className="text-xs text-gray-500 uppercase font-bold">
                  Institution
                </label>
                <div className="text-sm font-medium text-gray-900">
                  {institutionName}
                </div>
              </div>

              <div>
                <label className="text-xs text-gray-500 uppercase font-bold">
                  Course of Study
                </label>
                <div className="text-sm font-medium text-gray-900">
                  {internData?.application?.ApplicationsAcademicQualification
                    ?.course?.name ||
                    internData?.application?.ApplicationNYSCDetails?.course
                      ?.name ||
                    "N/A"}
                </div>
              </div>

              <div>
                <label className="text-xs text-gray-500 uppercase font-bold">
                  Program Type
                </label>
                <div className="text-sm font-medium uppercase text-gray-900">
                  {internData?.application?.vacancy?.vacancy_position?.name ||
                    (internData?.vacancy_position_id === 1
                      ? "SIWES"
                      : internData?.vacancy_position_id === 2
                        ? "NYSC"
                        : "N/A")}
                </div>
              </div>

              <div>
                <label className="text-xs text-gray-500 uppercase font-bold">
                  Duration
                </label>
                <div className="text-sm font-medium text-gray-900">
                  {formattedDuration}
                </div>
              </div>

              <div>
                <label className="text-xs text-gray-500 uppercase font-bold">
                  Department
                </label>
                <div className="text-sm font-medium text-gray-900">
                  {(() => {
                    const deptCode =
                      internData?.application?.Applicant?.InternshipProgram?.[0]
                        ?.department;
                    // First try to look up from departments map
                    if (deptCode && departmentsMap[deptCode]) {
                      return departmentsMap[deptCode];
                    }
                    // If it doesn't look like a code, it might already be a name
                    if (deptCode && !/^\d+$/.test(deptCode)) {
                      return deptCode;
                    }
                    return "Pending";
                  })()}
                </div>
              </div>

              <div>
                <label className="text-xs text-gray-500 uppercase font-bold">
                  Business Unit
                </label>
                <div className="text-sm font-medium text-gray-900">
                  {internData?.application?.vacancy?.business_unit?.name ||
                    "N/A"}
                </div>
              </div>

              <div>
                <label className="text-xs text-gray-500 uppercase font-bold">
                  Location
                </label>
                <div className="text-sm font-medium text-gray-900">
                  {internData?.application?.vacancy?.location?.name || "N/A"}
                </div>
              </div>

              <div>
                <label className="text-xs text-gray-500 uppercase font-bold">
                  Date Joined
                </label>
                <div className="text-sm font-medium text-gray-900">
                  {internData?.created_at
                    ? new Date(internData.created_at).toLocaleDateString(
                        "en-GB",
                        {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        },
                      )
                    : "N/A"}
                </div>
              </div>
            </div>
          </div>
        </div>

        {canAssign ? (
          <div className="pt-2">
            <button
              onClick={handleOpenAssignDialog}
              disabled={!internData}
              className="w-full py-3 px-4 inline-flex justify-center items-center gap-2 rounded-md border border-transparent font-semibold bg-[#159351] text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-all text-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {hasSupervisor ? "Re-Assign Supervisor" : "Assign Supervisor"}
            </button>

            <Dialog
              open={isDialogOpen}
              onOpenChange={(open) => {
                setIsDialogOpen(open);
                if (!open) setSelectedSupervisor(null);
              }}
            >
              <DialogContent className="max-w-md w-full">
                <DialogHeader>
                  <DialogTitle>Select a Supervisor</DialogTitle>
                </DialogHeader>

                <div className="my-2">
                  <Input
                    placeholder={
                      searchMode === "staffId"
                        ? "Enter staff ID..."
                        : "Search supervisor name..."
                    }
                    value={lookupInput}
                    onChange={(e) => {
                      setLookupInput(e.target.value);
                      setSelectedSupervisor(null);
                    }}
                  />
                </div>

                <div className="flex items-center gap-2 mb-2">
                  <button
                    type="button"
                    className={`px-3 py-1 rounded text-xs font-semibold border ${
                      searchMode === "name"
                        ? "bg-green-100 text-green-800 border-green-300"
                        : "bg-white text-gray-600 border-gray-300"
                    }`}
                    onClick={() => {
                      setSearchMode("name");
                      setLookupInput("");
                      setSelectedSupervisor(null);
                    }}
                  >
                    Name
                  </button>
                  <button
                    type="button"
                    className={`px-3 py-1 rounded text-xs font-semibold border ${
                      searchMode === "staffId"
                        ? "bg-green-100 text-green-800 border-green-300"
                        : "bg-white text-gray-600 border-gray-300"
                    }`}
                    onClick={() => {
                      setSearchMode("staffId");
                      setLookupInput("");
                      setSelectedSupervisor(null);
                    }}
                  >
                    Staff ID
                  </button>
                </div>

                <div className="mt-2 h-[40vh] overflow-y-auto pr-2">
                  {loadingAllSupervisors || isLookupLoading ? (
                    <div className="text-center py-4 text-gray-500">
                      Loading staff...
                    </div>
                  ) : !lookupParams ? (
                    <div className="py-6 text-center text-gray-500 text-sm">
                      {searchMode === "name"
                        ? "Type at least 2 characters to search by name."
                        : "Enter a staff ID to search."}
                    </div>
                  ) : lookupError ? (
                    <div className="py-6 text-center text-red-500 text-sm">
                      {lookupError?.response?.data?.error ||
                        "Failed to search external staff."}
                    </div>
                  ) : externalLookupResults.length > 0 ? (
                    externalLookupResults.map((person) => (
                      <div
                        key={person.staffId}
                        className={`p-3 mb-2 transition-colors border rounded-md cursor-pointer group ${
                          selectedSupervisor?.staffId === person.staffId
                            ? "bg-green-50 border-[#159351]"
                            : "hover:bg-green-50 hover:border-green-200"
                        }`}
                        onClick={() => handleSelectSupervisor(person)}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h3
                              className={
                                selectedSupervisor?.staffId === person.staffId
                                  ? "text-green-800"
                                  : "text-gray-800 group-hover:text-green-800"
                              }
                            >
                              <span className="font-semibold">
                                {person.fullName || "Unknown Name"}
                              </span>{" "}
                              -{" "}
                              <span className="text-sm text-gray-500 font-mono">
                                {person.staffId || "N/A"}
                              </span>
                            </h3>
                            <p className="text-xs text-gray-500 my-1">
                              {person.email || "No email"}
                            </p>
                            <p className="text-xs text-gray-600">
                              {person.divisionName || "N/A"} -{" "}
                              {person.businessUnit || "N/A"}
                            </p>
                          </div>
                          {selectedSupervisor?.staffId === person.staffId && (
                            <div className="w-5 h-5 bg-[#159351] rounded-full flex items-center justify-center">
                              <svg
                                className="w-3 h-3 text-white"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={3}
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="py-6 text-center text-gray-500 text-sm">
                      No supervisors found for "{lookupInput}"
                    </div>
                  )}
                </div>

                {selectedSupervisor && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="bg-green-50 p-3 rounded-md mb-3">
                      <p className="text-xs text-gray-500 uppercase font-bold mb-1">
                        Selected Supervisor
                      </p>
                      <p className="font-semibold text-green-800">
                        {selectedSupervisor.fullName ||
                          selectedSupervisor.staffId}
                      </p>
                      <p className="text-xs text-gray-600">
                        {selectedSupervisor.email || "No email"}
                      </p>
                    </div>
                    <button
                      onClick={handleConfirmAssignment}
                      disabled={assignSupervisorMutation.isPending}
                      className="w-full py-2.5 px-4 inline-flex justify-center items-center gap-2 rounded-md border border-transparent font-semibold bg-[#159351] text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-all text-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      {assignSupervisorMutation.isPending ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Assigning...
                        </>
                      ) : hasSupervisor ? (
                        "Confirm Re-Assignment"
                      ) : (
                        "Confirm Assignment"
                      )}
                    </button>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </div>
        ) : (
          <div className="text-center p-4 bg-gray-50 rounded text-gray-500 font-medium">
            You do not have permission to assign supervisors.
          </div>
        )}
      </div>
    </div>
  );
}
